import { useState } from 'react';
import { MapPin, Clock, Phone, Navigation, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { locations } from '../data/menuData';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function LocationsPage() {
  const { selectedLocation, setLocation } = useCart();
  const [filter, setFilter] = useState('all');

  const handleSelectLocation = (location) => {
    setLocation(location);
    toast.success(`Sucursal ${location.name} seleccionada`, {
      description: `Tiempo estimado: ${location.avgPrepTime} min`,
    });
  };

  // Check if location is currently open
  const isOpen = (location) => {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const currentTime = hours * 60 + minutes;
    
    const [openHour, openMin] = location.hours.open.split(':').map(Number);
    const [closeHour, closeMin] = location.hours.close.split(':').map(Number);
    
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;
    
    return currentTime >= openTime && currentTime < closeTime;
  };

  return (
    <div className="min-h-screen pb-24 md:pb-12">
      {/* Header */}
      <div className="bg-secondary/30 border-b border-border/50">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8 md:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h1 className="font-heading text-3xl md:text-4xl font-medium tracking-tight">
              Nuestras Ubicaciones
            </h1>
            <p className="text-muted-foreground mt-2">
              {locations.length} sucursales en Monterrey y área metropolitana
            </p>
          </motion.div>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Locations List */}
          <div className="space-y-4">
            {locations.map((location, index) => {
              const open = isOpen(location);
              const isSelected = selectedLocation?.id === location.id;
              
              return (
                <motion.div
                  key={location.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`p-6 rounded-xl border transition-all cursor-pointer ${
                    isSelected
                      ? 'border-accent bg-accent/5 shadow-lg'
                      : 'border-border/50 hover:border-accent/50 hover:shadow-md'
                  }`}
                  onClick={() => handleSelectLocation(location)}
                  data-testid={`location-card-${location.id}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-medium text-lg">{location.name}</h3>
                        {isSelected && (
                          <Badge className="bg-accent text-accent-foreground">
                            <Check className="w-3 h-3 mr-1" />
                            Seleccionada
                          </Badge>
                        )}
                      </div>
                      
                      <div className="space-y-2 text-sm text-muted-foreground">
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          <span>{location.address}</span>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          <span>
                            {location.hours.open} - {location.hours.close}
                          </span>
                          <Badge 
                            variant={open ? "default" : "secondary"}
                            className={open ? "bg-green-100 text-green-800" : ""}
                          >
                            {open ? 'Abierto' : 'Cerrado'}
                          </Badge>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <Phone className="w-4 h-4" />
                          <span>{location.phone}</span>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 mt-4">
                        <Badge variant="secondary">
                          ~{location.avgPrepTime} min prep
                        </Badge>
                        <Badge variant="secondary">
                          Radio: {location.deliveryRadius} km
                        </Badge>
                        <Badge variant="secondary">
                          Envío gratis +${location.freeDeliveryMin}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-2xl font-semibold text-accent">
                        {location.avgPrepTime}
                      </p>
                      <p className="text-xs text-muted-foreground">min aprox</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 mt-4 pt-4 border-t border-border/50">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="rounded-full flex-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://maps.google.com/?q=${location.coordinates.lat},${location.coordinates.lng}`, '_blank');
                      }}
                    >
                      <Navigation className="w-4 h-4 mr-1" />
                      Cómo llegar
                    </Button>
                    <Link to="/menu" className="flex-1" onClick={(e) => e.stopPropagation()}>
                      <Button 
                        size="sm" 
                        className="rounded-full w-full"
                        onClick={() => handleSelectLocation(location)}
                      >
                        Pedir aquí
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Selected location info card */}
          <div className="lg:sticky lg:top-24 h-fit space-y-4">
            {selectedLocation ? (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-6 bg-card rounded-2xl border border-border/50 shadow-lg"
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 bg-accent/10 rounded-xl flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-7 h-7 text-accent" />
                  </div>
                  <div className="flex-1">
                    <Badge className="bg-accent text-accent-foreground mb-2">
                      <Check className="w-3 h-3 mr-1" />
                      Seleccionada
                    </Badge>
                    <h3 className="font-heading text-xl font-medium">{selectedLocation.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">{selectedLocation.address}</p>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4 mt-6">
                  <div className="p-4 bg-secondary/50 rounded-xl text-center">
                    <p className="text-3xl font-semibold text-accent">{selectedLocation.avgPrepTime}</p>
                    <p className="text-xs text-muted-foreground mt-1">min preparación</p>
                  </div>
                  <div className="p-4 bg-secondary/50 rounded-xl text-center">
                    <p className="text-3xl font-semibold">${selectedLocation.freeDeliveryMin}</p>
                    <p className="text-xs text-muted-foreground mt-1">envío gratis</p>
                  </div>
                </div>

                <div className="flex gap-3 mt-6">
                  <Button 
                    variant="outline" 
                    className="flex-1 rounded-full"
                    onClick={() => window.open(`https://maps.google.com/?q=${selectedLocation.coordinates.lat},${selectedLocation.coordinates.lng}`, '_blank')}
                  >
                    <Navigation className="w-4 h-4 mr-2" />
                    Cómo llegar
                  </Button>
                  <Link to="/menu" className="flex-1">
                    <Button className="w-full rounded-full">
                      Pedir ahora
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </motion.div>
            ) : (
              <div className="p-8 bg-secondary/30 rounded-2xl border border-border/50 text-center">
                <div className="w-16 h-16 bg-secondary rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-medium text-lg">Selecciona una sucursal</h3>
                <p className="text-sm text-muted-foreground mt-2">
                  Elige la sucursal más cercana para ver detalles y hacer tu pedido
                </p>
              </div>
            )}

            {/* Quick info */}
            <div className="p-6 bg-primary text-primary-foreground rounded-2xl">
              <h4 className="font-medium mb-4">¿Por qué elegirnos?</h4>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-3">
                  <Clock className="w-5 h-5 text-accent" />
                  Entrega promedio en 25 min
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-accent" />
                  Seguimiento en tiempo real
                </li>
                <li className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-accent" />
                  Soporte 24/7 vía WhatsApp
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
